import {
  WSAlert,
  WSAlertProps,
  WSButton,
  WSElementProps
} from "@wingspanhq/fe-component-library";
import React, { useRef, useState } from "react";
import { useHistory } from "react-router-dom";
import { PATH_SETTINGS_ACCOUNT_ACCOUNT } from "../../modules/Settings/paths";
import { ErrorContextKey, PlatformErrorRegexp } from "../../services/platform";
import { IS_DEV_ENV } from "../../shared/constants/environment";
import { openIntercomMessage } from "../../shared/utils/intercom";
import {
  ContactSupportButton,
  platformErrors
} from "../../utils/platformErrors";

const OTP_SUPPORT_MESSAGE =
  "Hi there! I'm having trouble with completing two-factor authentication. Could you help me with it";

const getErrorSubType = (error: any): string | undefined =>
  error?.response?.data?.subtype || error?.response?.data?.errorSubType;

const isOtpRequiredError = (error: any): boolean =>
  getErrorSubType(error) === "OtpRequired";

type TransformParams = {
  error?: any;
  contextKey: keyof typeof ErrorContextKey;
  forceShowApiErrors?: boolean;
};

export type WSErrorMessageProps = TransformParams &
  WSElementProps &
  WSAlertProps;

export const transformErrorMessage = ({
  error,
  contextKey,
  forceShowApiErrors
}: TransformParams) => {
  let message: React.ReactNode = "";

  if (typeof error === "string") {
    message = error;
  } else if (typeof error === "object") {
    if (error.response) {
      // Api service error. Here error mapping file will be applied

      // Check global errors mapping for corresponding error message
      const type: string =
        error.response.data.type || error.response.data.errorType || "Other";
      const subtype: string =
        error.response.data.subtype ||
        error.response.data.errorSubType ||
        "Other";
      const errorMessage = error.response.data.error;
      if (platformErrors[contextKey]) {
        if (errorMessage && platformErrors[contextKey]?.errorRegexs) {
          message = getRegexpErrorMessage(
            platformErrors[contextKey]?.errorRegexs!,
            errorMessage
          );
        } else if (platformErrors[contextKey]?.wsErrors?.[type]) {
          // If not, lets check the wsErrors map
          if (
            React.isValidElement(platformErrors[contextKey]?.wsErrors[type])
          ) {
            message = platformErrors[contextKey]?.wsErrors[type];
          } else {
            message = (platformErrors[contextKey]?.wsErrors[type] as any)[
              subtype
            ];
          }
        }
      }

      // If there is no matching error in global mapping, just put api repsonse
      if (!message) {
        if (IS_DEV_ENV) {
          message = `"${error?.response?.data.error}"`;
        } else if (forceShowApiErrors) {
          message = error?.response?.data.error;
        } else {
          console.error(`WS API ERROR "${error?.response?.data.error}"`);
        }
      }
    } else {
      // Api service error without data
      // It means something is wrong on network level
      // Examples: No internet connection, CORS error

      if (error.message === "Network Error") {
        message =
          "We're having trouble communicating with our servers. Please check your internet connection and try again!";
      }

      if (!message) {
        if (IS_DEV_ENV) {
          message = `"${error.message}"`;
        } else if (forceShowApiErrors) {
          message = error.message;
        } else {
          console.error(`WS ERROR "${error.message}"`);
        }
      }
    }
  }

  if (!message) {
    message = (
      <>
        Sorry! Something went wrong here. Please try again or{" "}
        <ContactSupportButton>contact our support</ContactSupportButton> if this
        issue continues.
      </>
    );
  }

  return message;
};

export const WSErrorMessage: React.FC<WSErrorMessageProps> = ({
  error,
  contextKey,
  forceShowApiErrors,
  ...elementProps
}) => {
  const history = useHistory();
  const [isDismissed, setIsDismissed] = useState(false);
  const prevErrorRef = useRef(error);

  if (error !== prevErrorRef.current) {
    prevErrorRef.current = error;
    if (isDismissed) {
      setIsDismissed(false);
    }
  }

  if (!error || isDismissed) {
    return null;
  }

  if (isOtpRequiredError(error)) {
    return (
      <WSAlert
        {...elementProps}
        theme="error"
        icon="info-circle"
        title="Verification incomplete"
        onDismiss={() => setIsDismissed(true)}
        data-testid="ws-form-api-error"
      >
        <>
          Two-factor verification is required to continue. Please try again.
          <br />
          Still having issues?
          <br />
          {"· Check that your phone number and email are up to date. "}
          <WSButton.Link
            onClick={() => history.push(PATH_SETTINGS_ACCOUNT_ACCOUNT)}
            type="button"
          >
            Go to account settings
          </WSButton.Link>
          <br />
          {"· Need help? "}
          <WSButton.Link
            onClick={() => openIntercomMessage(OTP_SUPPORT_MESSAGE)}
            type="button"
          >
            Contact support
          </WSButton.Link>
        </>
      </WSAlert>
    );
  }

  const message = transformErrorMessage({
    error,
    contextKey,
    forceShowApiErrors
  });

  return (
    <WSAlert
      {...elementProps}
      theme="error"
      icon="info-circle"
      data-testid="ws-form-api-error"
    >
      <>{message}</>
    </WSAlert>
  );
};

function getRegexpErrorMessage(
  regexps: PlatformErrorRegexp[],
  originalMessage: string
) {
  for (let regexp of regexps) {
    if (new RegExp(regexp.pattern).test(originalMessage)) {
      if (typeof regexp.message === "string") {
        return originalMessage.replace(
          new RegExp(regexp.pattern),
          regexp.message
        );
      } else {
        return regexp.message;
      }
    }
  }
}
